import "@/App.css";
import { useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/admin/ProtectedRoute";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import FloatingCall from "@/components/site/FloatingCall";
import Home from "@/pages/Home";
const CityPage = lazy(() => import("@/pages/CityPage"));
const Blog = lazy(() => import("@/pages/Blog"));
const BlogPost = lazy(() => import("@/pages/BlogPost"));
const AdminLogin = lazy(() => import("@/pages/AdminLogin"));
const AdminDashboard = lazy(() => import("@/pages/AdminDashboard"));
const Faq = lazy(() => import("@/pages/Faq"));
const Privacy = lazy(() => import("@/pages/Privacy"));
const Terms = lazy(() => import("@/pages/Terms"));
const ServicePage = lazy(() => import("@/pages/ServicePage"));
const Report = lazy(() => import("@/pages/Report"));
const ThankYou = lazy(() => import("@/pages/ThankYou"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const Review = lazy(() => import("@/pages/Review"));
const ReviewCards = lazy(() => import("@/pages/ReviewCards"));
const SeoResults = lazy(() => import("@/pages/SeoResults"));
import TextUsTab from "@/components/site/TextUsTab";
import { resolveRedirect, withSearch } from "@/data/redirects";

function CatchAll() {
  const { pathname, search } = useLocation();
  const target = resolveRedirect(pathname);
  if (target) return <Navigate to={withSearch(target, search)} replace />;
  return <NotFound />;
}

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  const isReport = pathname.startsWith("/report") || pathname.startsWith("/seo-results");
  const isCards = pathname.startsWith("/review-cards");
  const bare = isAdmin || isReport || isCards;
  return (
    <>
      <ScrollToTop />
      <Toaster position="top-center" richColors />
      {!bare && <Header />}
      <main>
        <Suspense fallback={<div className="min-h-screen" data-testid="route-loading" />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/garage-door-repair/:slug" element={<CityPage />} />
          <Route path="/garage-door-tips" element={<Blog />} />
          <Route path="/garage-door-tips/:slug" element={<BlogPost />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/report" element={<Report />} />
          <Route path="/seo-results" element={<SeoResults />} />
          <Route path="/thank-you" element={<ThankYou />} />
          <Route path="/review" element={<Review />} />
          <Route path="/review-cards" element={<ReviewCards />} />
          <Route path="/reviews" element={<Navigate to="/review" replace />} />
          <Route path="/blog" element={<Navigate to="/garage-door-tips" replace />} />
          <Route path="/blog/:slug" element={<Navigate to="/garage-door-tips" replace />} />
          <Route path="/contact" element={<CatchAll />} />
          <Route path="/contact/*" element={<CatchAll />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<ProtectedRoute><AdminDashboard /></ProtectedRoute>} />
          <Route path="/:serviceSlug/:citySlug" element={<ServicePage />} />
          <Route path="*" element={<CatchAll />} />
        </Routes>
        </Suspense>
      </main>
      {!bare && <Footer />}
      {!bare && <FloatingCall />}
      {!bare && <TextUsTab />}
    </>
  );
}

function App() {
  return (
    <div className="App" data-testid="app-root">
      <BrowserRouter>
        <AuthProvider>
          <Shell />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
